/* eslint-disable camelcase */
import { Typography, Box, styled, Paper } from '@mui/material';
import automotorImg from '../assets/imponible-automotor.png';
import cementerioImg from '../assets/imponible-cementerio.png';
import comercioImg from '../assets/imponible-comercio.png';
import contribuyenteImg from '../assets/imponible-contribuyente.png';
import inmobiliarioImg from '../assets/imponible-inmobiliario.png';
import { ImponibleData } from '../features/imponibles/imponiblesAPI';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import ResourceIcon from '../assets/icons/resource-icon';
import { getLabelClaveComprobante, getLabelTipoComprobante } from '../utils';

interface ImponibleProps {
  imponible: ImponibleData;
}

const ImponibleWrapper = styled(Link)<{ component: 'Link' }>(({ theme }) => ({
  position: 'relative',
  border: '1px solid',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '11rem',
  minHeight: '15rem',
  cursor: 'pointer',
  textDecoration: 'none',
  borderRadius: '5px',
  marginBottom: '1rem',

  [theme.breakpoints.down('sm')]: {
    marginRight: '0',
  },
}));

const AbsolutWrapper = styled(Paper)(() => ({
  top: 0,
  left: 0,
  position: 'absolute',
  padding: '1rem',
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  backgroundColor: 'transparent',
  marginBottom: '1rem',
}));

const RelativeWrapper = styled(Paper)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  transition: 'opacity 200ms ease-out',
  padding: '0 1rem',
  backgroundColor: 'transparent',
}));

const Text = styled(Typography)(() => ({
  fontSize: '.624rem',
  color: 'gray',
}));

const getImageImponible = (tipo: string) => {
  switch (tipo) {
    case 'ROD': return automotorImg;
    case 'CEM': return cementerioImg;
    case 'INM': return inmobiliarioImg;
    case 'COM': return comercioImg;
    default: return contribuyenteImg;
  }
};

const getLabelImponible = (imponible: ImponibleData) => {
  switch (imponible.tipo) {
    case 'ROD': return imponible.informacion.dominio;
    case 'CEM': return imponible.informacion.nro_sepult;
    case 'INM': return imponible.informacion.par_catastral;
    case 'COM': return imponible.informacion.nro_comercio;
    default: return imponible.informacion.cuim;
  }
};

const hexToRgba = (hex: string, alpha: number) => {
  const colorWithOutHash = hex.substring(1);
  const r = parseInt(colorWithOutHash.substring(0, 2), 16);
  const g = parseInt(colorWithOutHash.substring(2, 4), 16);
  const b = parseInt(colorWithOutHash.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export default function Imponible(props: ImponibleProps) {
  const { imponible } = props;
  const { id, tipo, recursos, referencia, color } = imponible;

  const [isShown, setIsShown] = useState(false);

  return (
    <ImponibleWrapper
      component="Link"
      to={`/imponibles/${id}`}
      onMouseEnter={() => setIsShown(!isShown)}
      onMouseLeave={() => setIsShown(!isShown)}
      sx={{
        borderColor: isShown ? color : '#E4E6EF',
        backgroundColor: isShown ? hexToRgba(color, 0.2) : 'white',
      }}
    >
      {isShown && (
        <AbsolutWrapper sx={{ opacity: isShown ? 1 : 0 }}>
          <Box mb={1}>
            <Typography variant={'subtitle1'} sx={{ lineHeight: 1.2 }}>
              {referencia}
            </Typography>
            <Typography mt={0.2} mb={2}> { getLabelTipoComprobante(tipo) }
            </Typography>

            {recursos.map((recurso, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center' }}>
                <ResourceIcon color={color} />
                <Text sx={{ marginLeft: '0.2rem' }}>{recurso.descripcion}</Text>
              </Box>
            ))}
          </Box>

          <Typography variant={'body2'}>Ver Todos</Typography>
        </AbsolutWrapper>
      )}

      <RelativeWrapper sx={{ opacity: isShown ? 0 : 1 }}>
        <img
          src={ getImageImponible(tipo) }
          style={{
            maxWidth: '100%',
            height: 'auto',
            marginBottom: '1rem',
          }}
        />
        <Box>
          <Typography
            variant="subtitle1"
            mb={1}
            sx={{ textAlign: 'center', lineHeight: 1.2 }}
          >
            {referencia}
          </Typography>
          <Typography variant="body1" sx={{ textAlign: 'center' }}>
            { getLabelTipoComprobante(tipo) }
          </Typography>
          <Typography
            mt={2}
            variant="body2"
            sx={{ textAlign: 'center', fontSize: '0.8rem' }}
          >
            { getLabelClaveComprobante(tipo) }
          </Typography>
          <Typography
            variant="body2"
            sx={{ textAlign: 'center', fontSize: '0.8rem' }}
          >
            <strong>
              { getLabelImponible(imponible) }
            </strong>
          </Typography>
        </Box>
      </RelativeWrapper>
    </ImponibleWrapper>
  );
}
